import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Terminal, FileText, Plug, Activity,
  Play, Pause, Trash2, Download, RefreshCw,
  ChevronDown, ChevronRight, AlertTriangle, CheckCircle, XCircle,
  Clock, Wifi, WifiOff, Server, Search, Filter, X
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatTs = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

const timeAgo = (ts) => {
  if (!ts) return 'never';
  const secs = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (secs < 60)   return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const classifyLine = (line) => {
  const l = line.toLowerCase();
  if (l.includes('error') || l.includes('err]') || l.includes('failed') || l.includes('crash')) return 'error';
  if (l.includes('warn') || l.includes('warning')) return 'warn';
  if (l.includes('info]') || l.includes(' info ')) return 'info';
  if (l.includes('debug]')) return 'debug';
  return 'default';
};

const LineColors = {
  error:   '#f87171',
  warn:    '#fbbf24',
  info:    '#34d399',
  debug:   '#9ca3af',
  default: '#d1d5db',
};

const StatusDot = ({ status, size = 10 }) => {
  const colors = { ok: '#34d399', error: '#f87171', warning: '#fbbf24', unknown: '#6b7280', offline: '#f87171', online: '#34d399' };
  const color = colors[status] || colors.unknown;
  return (
    <span style={{
      display: 'inline-block', width: size, height: size,
      borderRadius: '50%', background: color, flexShrink: 0,
      boxShadow: `0 0 6px ${color}80`,
    }} />
  );
};

// ─── 1. Server Logs Tab ───────────────────────────────────────────────────────
const ServerLogsTab = () => {
  const [lines, setLines]       = useState([]);
  const [paused, setPaused]     = useState(false);
  const [filter, setFilter]     = useState('');
  const [connected, setConnected] = useState(false);
  const [inputFilter, setInputFilter] = useState('');
  const esRef   = useRef(null);
  const bottomRef = useRef(null);
  const pausedRef = useRef(false);
  const MAX_LINES = 1000;

  const connect = useCallback((filterVal = '') => {
    if (esRef.current) { esRef.current.close(); }
    setLines([]);
    setConnected(false);
    const url = `/api/platform/diagnostics/logs/stream?lines=200&filter=${encodeURIComponent(filterVal)}`;
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;

    es.onmessage = (e) => {
      if (pausedRef.current) return;
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'connected') { setConnected(true); return; }
        if (data.type === 'closed')    { setConnected(false); return; }
        if (data.line) {
          setLines(prev => {
            const next = [...prev, { id: Date.now() + Math.random(), line: data.line, level: classifyLine(data.line) }];
            return next.length > MAX_LINES ? next.slice(-MAX_LINES) : next;
          });
        }
      } catch (_) {}
    };
    es.onerror = () => setConnected(false);
  }, []);

  useEffect(() => {
    connect('');
    return () => { if (esRef.current) esRef.current.close(); };
  }, [connect]);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    if (!paused && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [lines, paused]);

  const applyFilter = () => { setFilter(inputFilter); connect(inputFilter); };
  const clearFilter = () => { setInputFilter(''); setFilter(''); connect(''); };

  const exportLogs = () => {
    const text = lines.map(l => l.line).join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `serverliccm-logs-${new Date().toISOString().slice(0,19)}.txt`;
    a.click();
  };

  return (
    <div className="diag-section">
      <div className="diag-toolbar">
        <div className="diag-search">
          <Filter size={14} />
          <input
            value={inputFilter}
            onChange={e => setInputFilter(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && applyFilter()}
            placeholder="Filter logs (e.g. error, [MD-WS], auth) — press Enter"
          />
          {filter && <button className="diag-clear-btn" onClick={clearFilter}><X size={12}/></button>}
        </div>
        <div className="diag-actions">
          <span className={`diag-status-badge ${connected ? 'connected' : 'disconnected'}`}>
            {connected ? <><Wifi size={12}/> Live</> : <><WifiOff size={12}/> Disconnected</>}
          </span>
          <span className="diag-line-count">{lines.length} lines</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setPaused(p => !p)}>
            {paused ? <><Play size={14}/> Resume</> : <><Pause size={14}/> Pause</>}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setLines([])}>
            <Trash2 size={14}/> Clear
          </button>
          <button className="btn btn-secondary btn-sm" onClick={exportLogs}>
            <Download size={14}/> Export
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => connect(filter)}>
            <RefreshCw size={14}/> Reconnect
          </button>
        </div>
      </div>

      {filter && (
        <div className="diag-filter-badge">
          Filtering: <code>{filter}</code>
        </div>
      )}

      <div className="diag-log-terminal">
        {lines.length === 0 && (
          <div className="diag-log-empty">
            {connected ? 'Waiting for log output...' : 'Connecting to log stream...'}
          </div>
        )}
        {lines.map(({ id, line, level }) => (
          <div key={id} className="diag-log-line" style={{ color: LineColors[level] }}>
            <span className="diag-log-text">{line}</span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

// ─── 2. Audit Logs Tab ────────────────────────────────────────────────────────
const AuditLogsTab = ({ tenants = [] }) => {
  const [logs, setLogs]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters]   = useState({ tenantId: '', action: '', severity: '', search: '', from: '', to: '' });

  const fetchLogs = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: 50 });
      if (filters.tenantId) params.set('tenantId', filters.tenantId);
      if (filters.action)   params.set('action',   filters.action);
      if (filters.severity) params.set('severity', filters.severity);
      if (filters.search)   params.set('search',   filters.search);
      if (filters.from)     params.set('from',     filters.from);
      if (filters.to)       params.set('to',       filters.to);

      const res = await fetch(`/api/platform/diagnostics/audit?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setLogs(data.logs || []);
      setTotal(data.pagination?.total || 0);
      setPage(pg);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchLogs(1); }, [fetchLogs]);

  const severityColor = (s) => ({ ERROR: '#f87171', WARN: '#fbbf24', INFO: '#60a5fa', DEBUG: '#9ca3af' }[s] || '#6b7280');

  const exportCsv = () => {
    const headers = ['Timestamp', 'Tenant', 'User', 'Action', 'IP', 'Severity'];
    const rows = logs.map(l => [
      formatTs(l.createdAt),
      l.tenant?.name || l.tenantId || '',
      l.user?.email || l.userId || '',
      l.action || '',
      l.ipAddress || '',
      l.severity || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `audit-logs-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  return (
    <div className="diag-section">
      <div className="diag-toolbar diag-toolbar-wrap">
        <div className="diag-search">
          <Search size={14}/>
          <input value={filters.search} onChange={e => setFilters(f => ({...f, search: e.target.value}))} placeholder="Search action, description, IP..." />
        </div>
        <select className="diag-select" value={filters.tenantId} onChange={e => setFilters(f => ({...f, tenantId: e.target.value}))}>
          <option value="">All Sites</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="diag-select" value={filters.severity} onChange={e => setFilters(f => ({...f, severity: e.target.value}))}>
          <option value="">All Severity</option>
          {['INFO','WARN','ERROR','DEBUG'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="datetime-local" className="diag-input" value={filters.from} onChange={e => setFilters(f => ({...f, from: e.target.value}))} title="From date" />
        <input type="datetime-local" className="diag-input" value={filters.to} onChange={e => setFilters(f => ({...f, to: e.target.value}))} title="To date" />
        <button className="btn btn-primary btn-sm" onClick={() => fetchLogs(1)}>
          <Search size={14}/> Search
        </button>
        <button className="btn btn-secondary btn-sm" onClick={exportCsv}>
          <Download size={14}/> CSV
        </button>
      </div>

      {loading ? (
        <div className="diag-loading"><div className="diag-spinner"/></div>
      ) : (
        <>
          <div className="diag-table-meta">{total} records</div>
          <div className="diag-table-wrap">
            <table className="diag-table">
              <thead>
                <tr>
                  <th>Timestamp</th><th>Site</th><th>User</th>
                  <th>Action</th><th>IP</th><th>Severity</th><th></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr><td colSpan={7} className="diag-empty-cell">No audit logs found for the selected filters.</td></tr>
                )}
                {logs.map(log => (
                  <React.Fragment key={log.id}>
                    <tr className="diag-table-row" onClick={() => setExpanded(expanded === log.id ? null : log.id)}>
                      <td className="diag-mono">{formatTs(log.createdAt)}</td>
                      <td>{log.tenant?.name || <span className="diag-muted">{log.tenantId?.slice(0,8)}</span>}</td>
                      <td>{log.user?.email || <span className="diag-muted">{log.userId?.slice(0,8) || '—'}</span>}</td>
                      <td className="diag-action-cell">{log.action}</td>
                      <td className="diag-mono diag-muted">{log.ipAddress || '—'}</td>
                      <td><span className="diag-severity-badge" style={{ color: severityColor(log.severity), borderColor: severityColor(log.severity) + '40', background: severityColor(log.severity) + '15' }}>{log.severity || 'INFO'}</span></td>
                      <td>{expanded === log.id ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}</td>
                    </tr>
                    {expanded === log.id && (
                      <tr className="diag-expand-row">
                        <td colSpan={7}>
                          <pre className="diag-json-pre">{JSON.stringify({ description: log.description, metadata: log.metadata, resourceId: log.resourceId, resourceType: log.resourceType }, null, 2)}</pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {total > 50 && (
            <div className="diag-pagination">
              <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => fetchLogs(page - 1)}>← Prev</button>
              <span>Page {page} of {Math.ceil(total / 50)}</span>
              <button className="btn btn-secondary btn-sm" disabled={page >= Math.ceil(total / 50)} onClick={() => fetchLogs(page + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── 3. Integration Health Tab ────────────────────────────────────────────────
const IntegrationHealthTab = () => {
  const [data, setData]         = useState({ tenants: [], agents: [] });
  const [loading, setLoading]   = useState(false);
  const [checking, setChecking] = useState({});

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/platform/diagnostics/integrations', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      setData(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchHealth(); }, []);

  const runCheck = async (integrationId, tenantIdx, integIdx) => {
    setChecking(c => ({ ...c, [integrationId]: true }));
    try {
      const res = await fetch('/api/platform/diagnostics/integrations/check', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ integrationId }),
      });
      const result = await res.json();
      setData(prev => {
        const updatedTenants = prev.tenants.map((t, ti) => {
          if (ti !== tenantIdx) return t;
          const updatedIntegrations = t.integrations.map((ig, ii) => {
            if (ii !== integIdx) return ig;
            return { ...ig, status: result.ok ? 'ok' : 'error', lastError: result.error || null,
              lastChecked: new Date().toISOString(), latencyMs: result.latencyMs };
          });
          return { ...t, integrations: updatedIntegrations };
        });
        return { ...prev, tenants: updatedTenants };
      });
    } catch (err) { console.error(err); }
    finally { setChecking(c => ({ ...c, [integrationId]: false })); }
  };

  const typeLabel = (t) => ({
    WAVE_VMS: 'Wave VMS', HANWHA_WAVE: 'Wave VMS', FEENICS: 'Feenics',
    OOSTO: 'Oosto', INTERCOM: '2N Intercom', '2N': '2N Intercom',
    MIRASYS: 'Mirasys', MILESTONE: 'Milestone',
  }[t] || t);

  if (loading) return <div className="diag-loading"><div className="diag-spinner"/></div>;

  return (
    <div className="diag-section">
      <div className="diag-toolbar">
        <span className="diag-muted">{data.tenants.length} sites with active integrations</span>
        <button className="btn btn-secondary btn-sm" onClick={fetchHealth}><RefreshCw size={14}/> Refresh</button>
      </div>

      {data.tenants.length === 0 && (
        <div className="diag-empty-state"><Plug size={32}/><p>No active integrations configured.</p></div>
      )}

      <div className="diag-health-grid">
        {data.tenants.map((tenant, ti) => (
          <div key={tenant.tenant.id} className="diag-health-card">
            <div className="diag-health-card-header">
              <Server size={16}/>
              <span className="diag-health-card-title">{tenant.tenant.name}</span>
            </div>
            <div className="diag-health-integrations">
              {tenant.integrations.map((integ, ii) => (
                <div key={integ.id} className="diag-health-row">
                  <StatusDot status={integ.status} />
                  <span className="diag-health-type">{typeLabel(integ.type)}</span>
                  {integ.config?.host && <span className="diag-muted diag-mono" style={{fontSize:11}}>{integ.config.host}</span>}
                  <div style={{flex:1}}/>
                  {integ.latencyMs && <span className="diag-latency">{integ.latencyMs}ms</span>}
                  {integ.lastChecked && <span className="diag-muted" style={{fontSize:11}}>{timeAgo(integ.lastChecked)}</span>}
                  <button
                    className="diag-check-btn"
                    disabled={!!checking[integ.id]}
                    onClick={() => runCheck(integ.id, ti, ii)}
                    title="Run live check"
                  >
                    {checking[integ.id] ? <div className="diag-spinner diag-spinner-sm"/> : <RefreshCw size={12}/>}
                  </button>
                </div>
              ))}
              {integ => integ?.lastError && (
                <div className="diag-health-error">{integ.lastError}</div>
              )}
            </div>
            {/* Render errors below each row */}
            {tenant.integrations.filter(ig => ig.lastError).map(ig => (
              <div key={ig.id + '-err'} className="diag-health-error-row">
                <AlertTriangle size={12}/> {typeLabel(ig.type)}: {ig.lastError}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* MD Agents summary */}
      {data.agents?.length > 0 && (
        <div style={{marginTop: 24}}>
          <h4 className="diag-section-subtitle">ServerliMD Agents</h4>
          <div className="diag-agents-list">
            {data.agents.map(agent => (
              <div key={agent.id} className="diag-agent-row">
                <StatusDot status={agent.status === 'online' ? 'ok' : 'error'}/>
                <span className="diag-agent-name">{agent.name || agent.id.slice(0,8)}</span>
                <span className="diag-muted">{agent.tenant?.name}</span>
                <span className="diag-muted diag-mono" style={{fontSize:11}}>{agent.os} · v{agent.version}</span>
                <div style={{flex:1}}/>
                <span className="diag-muted" style={{fontSize:12}}>{timeAgo(agent.lastHeartbeat)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── 4. MD Agent Logs Tab ─────────────────────────────────────────────────────
const MdAgentLogsTab = () => {
  const [data, setData]         = useState({ agents: [] });
  const [loading, setLoading]   = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/platform/diagnostics/md-agents', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      setData(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const statusColor = (s) => ({ online: '#34d399', offline: '#f87171', degraded: '#fbbf24' }[s] || '#6b7280');

  if (loading) return <div className="diag-loading"><div className="diag-spinner"/></div>;

  return (
    <div className="diag-section">
      <div className="diag-toolbar">
        <span className="diag-muted">{data.agents.length} agents registered</span>
        <button className="btn btn-secondary btn-sm" onClick={fetchData}><RefreshCw size={14}/> Refresh</button>
      </div>

      {data.agents.length === 0 && (
        <div className="diag-empty-state"><Activity size={32}/><p>No ServerliMD agents registered yet.</p></div>
      )}

      <div className="diag-agent-cards">
        {data.agents.map(agent => (
          <div key={agent.id} className="diag-agent-card">
            <div className="diag-agent-card-header" onClick={() => setExpanded(expanded === agent.id ? null : agent.id)}>
              <div className="diag-agent-card-title">
                <StatusDot status={agent.status === 'online' ? 'online' : 'offline'}/>
                <strong>{agent.name || 'Unnamed Agent'}</strong>
                <span className="diag-muted">{agent.tenant?.name}</span>
              </div>
              <div className="diag-agent-card-meta">
                <span className="diag-mono" style={{fontSize:12, color: statusColor(agent.status)}}>{agent.status}</span>
                <span className="diag-muted" style={{fontSize:12}}>{agent.os} · v{agent.version || '?'}</span>
                <span className="diag-muted" style={{fontSize:12}}>Last seen {timeAgo(agent.lastHeartbeat)}</span>
                {expanded === agent.id ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
              </div>
            </div>

            {/* Known issues */}
            {agent.knownIssues?.length > 0 && (
              <div className="diag-known-issues">
                {agent.knownIssues.map((issue, i) => (
                  <div key={i} className={`diag-issue diag-issue-${issue.severity}`}>
                    <AlertTriangle size={12}/>
                    <span>{issue.message}</span>
                  </div>
                ))}
              </div>
            )}

            {expanded === agent.id && (
              <div className="diag-agent-detail">
                {/* Recent alerts */}
                {agent.recentAlerts?.length > 0 && (
                  <div className="diag-agent-section">
                    <div className="diag-agent-section-title">Recent Alerts (24h)</div>
                    <table className="diag-table diag-table-sm">
                      <thead><tr><th>Time</th><th>Title</th><th>Severity</th><th>Status</th></tr></thead>
                      <tbody>
                        {agent.recentAlerts.slice(0, 10).map(alert => (
                          <tr key={alert.id}>
                            <td className="diag-mono">{formatTs(alert.createdAt)}</td>
                            <td>{alert.title}</td>
                            <td><span style={{color: alert.severity === 'CRITICAL' ? '#f87171' : alert.severity === 'WARNING' ? '#fbbf24' : '#60a5fa'}}>{alert.severity}</span></td>
                            <td><span className="diag-muted">{alert.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Monitored devices sample */}
                {agent.monitoredDevices?.length > 0 && (
                  <div className="diag-agent-section">
                    <div className="diag-agent-section-title">Monitored Devices (sample)</div>
                    <table className="diag-table diag-table-sm">
                      <thead><tr><th>Host</th><th>IP</th><th>Status</th><th>Last Seen</th></tr></thead>
                      <tbody>
                        {agent.monitoredDevices.map(dev => (
                          <tr key={dev.id}>
                            <td>{dev.hostname || '—'}</td>
                            <td className="diag-mono">{dev.ipAddress}</td>
                            <td><StatusDot status={dev.status === 'online' ? 'ok' : 'error'} size={8}/> {dev.status}</td>
                            <td className="diag-muted">{timeAgo(dev.lastSeen)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Root DiagnosticsTab ──────────────────────────────────────────────────────
const DiagnosticsTab = ({ tenants = [] }) => {
  const [subTab, setSubTab] = useState('logs');

  const subTabs = [
    { id: 'logs',         label: 'Server Logs',   icon: Terminal  },
    { id: 'audit',        label: 'Audit Logs',    icon: FileText  },
    { id: 'integrations', label: 'Integrations',  icon: Plug      },
    { id: 'md-agents',    label: 'MD Agents',     icon: Activity  },
  ];

  return (
    <div className="diag-root">
      <div className="diag-subtabs">
        {subTabs.map(t => (
          <button
            key={t.id}
            className={`diag-subtab ${subTab === t.id ? 'active' : ''}`}
            onClick={() => setSubTab(t.id)}
          >
            <t.icon size={15}/>
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {subTab === 'logs'         && <ServerLogsTab />}
      {subTab === 'audit'        && <AuditLogsTab tenants={tenants} />}
      {subTab === 'integrations' && <IntegrationHealthTab />}
      {subTab === 'md-agents'    && <MdAgentLogsTab />}
    </div>
  );
};

export default DiagnosticsTab;
